/**
 * IELTS overall band and indicative raw-score marks.
 *
 * Source: IELTS, "How IELTS is scored" / "IELTS scoring in detail",
 * https://ielts.org/take-a-test/your-results/ielts-scoring-in-detail (also
 * https://ielts.org/for-test-takers/how-ielts-is-scored). Retrieved 2026-10-16.
 *
 * Overall band rule (quoted by the page): the overall band score is the average of the four
 * section band scores rounded to the nearest half band; an average ending in .25 rounds up
 * to the next half band and one ending in .75 rounds up to the next whole band. The page's
 * worked examples: 6.5/6.5/5.0/7.0 -> 6.25 -> 6.5; 4.0/3.5/4.0/4.0 -> 3.875 -> 4.0;
 * 6.5/6.5/5.5/6.0 -> 6.125 -> 6.0.
 *
 * Raw scores: IELTS publishes only the average marks out of 40 at selected bands, and says
 * "the precise number of marks needed to achieve these band scores will vary slightly from
 * test version to test version". The full half-band conversion tables circulating online
 * are not published by IELTS and disagree with each other, so they are deliberately NOT
 * reproduced here.
 */

export type IeltsSection = "listening" | "reading" | "writing" | "speaking";
export const IELTS_SECTIONS: readonly IeltsSection[] = [
  "listening",
  "reading",
  "writing",
  "speaking",
];

/** A band is valid from 0 to 9 in half-band steps. */
export function isIeltsBand(value: number | null): value is number {
  return (
    value !== null &&
    Number.isFinite(value) &&
    value >= 0 &&
    value <= 9 &&
    Number.isInteger(value * 2)
  );
}

/**
 * Overall band from four section bands, by IELTS's rule. Works in half-band units so no
 * floating-point fraction can tip a .25 or .75 average the wrong way.
 */
export function ieltsOverall(bands: readonly number[]): number {
  const halves = bands.reduce((sum, band) => sum + Math.round(band * 2), 0);
  // average in half-bands = halves / 4; nearest half band, ties (.5 of a half) up.
  return Math.floor((halves + 2) / 4) / 2;
}

/** The unrounded average of the section bands. */
export function ieltsAverage(bands: readonly number[]): number {
  return bands.reduce((sum, band) => sum + band, 0) / bands.length;
}

export interface IeltsRounding {
  average: number;
  overall: number;
  /** How the average met the rule. */
  kind: "exact" | "down" | "up";
}

export function ieltsRounding(bands: readonly number[]): IeltsRounding {
  const average = ieltsAverage(bands);
  const overall = ieltsOverall(bands);
  let kind: IeltsRounding["kind"] = "exact";
  if (overall > average) {
    kind = "up";
  } else if (overall < average) {
    kind = "down";
  }
  return { average, overall, kind };
}

export interface IeltsFourthRow {
  band: number;
  overall: number;
}

/** The overall band for every possible band of the one section still to come. */
export function ieltsWithMissing(known: readonly number[]): IeltsFourthRow[] {
  const rows: IeltsFourthRow[] = [];
  for (let half = 0; half <= 18; half += 1) {
    const band = half / 2;
    rows.push({ band, overall: ieltsOverall([...known, band]) });
  }
  return rows;
}

/** The lowest band in the missing section that reaches `target`, or null if none does. */
export function ieltsNeeded(
  known: readonly number[],
  target: number
): number | null {
  const row = ieltsWithMissing(known).find((entry) => entry.overall >= target);
  return row ? row.band : null;
}

export type IeltsRawModule =
  | "listening"
  | "academic-reading"
  | "general-reading";

export interface IeltsAnchor {
  band: number;
  /** Average marks out of 40 that IELTS lists for the band. */
  marks: number;
}

/** IELTS's published average marks (out of 40), retrieved 2026-10-16. */
export const IELTS_ANCHORS: Record<IeltsRawModule, readonly IeltsAnchor[]> = {
  listening: [
    { band: 5, marks: 16 },
    { band: 6, marks: 23 },
    { band: 7, marks: 30 },
    { band: 8, marks: 35 },
  ],
  "academic-reading": [
    { band: 5, marks: 15 },
    { band: 6, marks: 23 },
    { band: 7, marks: 30 },
    { band: 8, marks: 35 },
  ],
  "general-reading": [
    { band: 4, marks: 15 },
    { band: 5, marks: 23 },
    { band: 6, marks: 30 },
    { band: 7, marks: 35 },
  ],
};

export interface IeltsRawPlacement {
  /** The highest listed band whose marks the score reaches, or null if below all. */
  reached: IeltsAnchor | null;
  /** The next listed band up, or null if the score is at or above the top one. */
  next: IeltsAnchor | null;
  /** Marks still needed to reach the next listed band. */
  toNext: number | null;
}

export function isRawScore(value: number | null): value is number {
  return value !== null && Number.isInteger(value) && value >= 0 && value <= 40;
}

/** Where `marks` falls against the listed averages for the module. */
export function ieltsRawPlacement(
  module: IeltsRawModule,
  marks: number
): IeltsRawPlacement {
  const anchors = IELTS_ANCHORS[module];
  let reached: IeltsAnchor | null = null;
  let next: IeltsAnchor | null = null;
  for (const anchor of anchors) {
    if (marks >= anchor.marks) {
      reached = anchor;
    } else if (next === null) {
      next = anchor;
    }
  }
  return { reached, next, toNext: next ? next.marks - marks : null };
}
